import useIsMobile from "@/hooks/useIsMobile";
import FieldingPage from "@/screens/IplPage/FieldingPage";
import dynamic from "next/dynamic";

const OrangeCapPage = dynamic(() => import("@/screens/IplPage/OrangeCap"));
const PurpleCapPage = dynamic(() => import("@/screens/IplPage/PurpleCap"));

const PageLayout = dynamic(() => import("@/screens/Layout/PageLayout"));

export default function Category({
  categoryNewsListData = null,
  latestStoryData = null,
  latestVideoData = null,
  pageId = null,
  year = 2025,
  faqData = null,
  statsData = null,
}) {
  const isMobileDevice = useIsMobile();
  return (
    <>
      <PageLayout
        rightSideBarData={categoryNewsListData}
        latestStoryData={latestStoryData}
        latestVideoData={latestVideoData}
        isMobileDevice={isMobileDevice}
      >
        {pageId == "fielding" ? (
          <FieldingPage
            categoryNewsListData={categoryNewsListData}
            isMobileDevice={isMobileDevice}
            faqData={faqData}
            year={year}
            statsData={statsData}
          />
        ) : pageId == "orange" ? (
          <OrangeCapPage
            categoryNewsListData={categoryNewsListData}
            isMobileDevice={isMobileDevice}
            faqData={faqData}
            year={year}
            statsData={statsData}
          />
        ) : (
          <PurpleCapPage
            categoryNewsListData={categoryNewsListData}
            isMobileDevice={isMobileDevice}
            faqData={faqData}
            year={year}
            statsData={statsData}
          />
        )}
      </PageLayout>
    </>
  );
}

export const getServerSideProps = async (ctx) => {
  const { logger } = await import("@/utils/logging/log-util");
  try {
    const { getDataByUrl } = await import("@/handlers/commonApi");
    const { getLatestContentFeed } = await import("@/handlers/content");
    const { getFaqBySlug } = await import("@/handlers/faq");
    const { getMasterConfig } = await import("@/handlers/masterConfig");
    const { getRedisApiKey, fetchRedisWrapper } = await import(
      "@/handlers/redisKey"
    );
    const { getStatsPlayer } = await import("@/handlers/ipl");
    const { setPageTTL } = await import("@/utils/setPageTTL");
    const { getUnixTimeDaysAgo } = await import("@/utils/dateTimeFormat");
    const unixtime = getUnixTimeDaysAgo(15);
    let masterConfigData = await getMasterConfig();

    //* get page from query param and subtract 1 from it , also error handle if not a number
    let page = ctx?.query?.page;

    if (isNaN(page)) {
      page = 0;
    }
    page = page ? parseInt(page) - 1 : 0;
    let url = ctx?.resolvedUrl;
    url = url.split("?")[0];

    let slug = ctx?.params?.slug;
    let slugParts = slug.split("-");
    // console.log("slug", (!slugParts || (slugParts[1] != "records" && slugParts[2] != "winner")));
    if (!slugParts || (slugParts[1] != "records" && slugParts[2] != "winner")) {
      return {
        redirect: {
          destination: "/sports/ipl",
          statusCode: 301,
        },
      };
    }

    let pageId = slugParts[0];
    let year = slugParts[slugParts.length - 1];

    const iplYearsFilter = {
      2025: "8307",
      2024: "5970",
      2023: "5157",
      2022: "4623",
      2021: "4165",
      2020: "3978",
      2019: "3130",
      2018: "2940",
    };
    if (slug?.startsWith("orange-cap-winner-")) {
      if (!iplYearsFilter[year]) {
        return {
          redirect: {
            destination: "/sports/ipl/orange-cap",
            statusCode: 301,
          },
        };
      }
      if (year == "2025" && pageId !== "orange-cap-winner-2025") {
        return {
          redirect: {
            destination: "/sports/ipl/orange-cap",
            statusCode: 301,
          },
        };
      }
    }

    // Purple Cap Redirection
    if (slug?.startsWith("purple-cap-winner-")) {
      if (!iplYearsFilter[year]) {
        return {
          redirect: {
            destination: "/sports/ipl/purple-cap",
            statusCode: 301,
          },
        };
      }
      if (year == "2025" && pageId !== "purple-cap-winner-2025") {
        return {
          redirect: {
            destination: "/sports/ipl/purple-cap",
            statusCode: 301,
          },
        };
      }
    }

    // fielding Redirection
    if (slug?.startsWith("fielding-records-")) {
      if (year == "2025" && pageId !== "fielding-records-2025") {
        return {
          redirect: {
            destination: "/sports/ipl/fielding-records",
            statusCode: 301,
          },
        };
      }
    }
    // invalid urls redirection
    const winners_urls = [
      "purple-cap-winner-2024",
      "orange-cap-winner-2024",
      "fielding-records-2024",
      "purple-cap-winner-2023",
      "orange-cap-winner-2023",
      "fielding-records-2023",
      "purple-cap-winner-2022",
      "orange-cap-winner-2022",
      "fielding-records-2022",
      "purple-cap-winner-2021",
      "orange-cap-winner-2021",
      "fielding-records-2021",
      "purple-cap-winner-2020",
      "orange-cap-winner-2020",
      "fielding-records-2020",
      "purple-cap-winner-2019",
      "orange-cap-winner-2019",
      "fielding-records-2019",
      "purple-cap-winner-2018",
      "orange-cap-winner-2018",
      "fielding-records-2018",
    ];

    if (!slug || !winners_urls.includes(slug)) {
      return {
        notFound: true,
      };
    }

    let categoryListRedisKey = getRedisApiKey({
      masterConfigData: masterConfigData,
      redisPrefix: "PREFIX_REDIS_ROS",
      params: {
        api: "getdatabyurl",
        url: "/sports",
        page: page,
        display: 6,
        type: "story",
      },
    });

    let faqRedisKey = getRedisApiKey({
      masterConfigData: masterConfigData,
      redisPrefix: "PREFIX_REDIS_ROS",
      params: { api: "staticpages", url: "ipl-2025-faqs" },
    });

    const latestStoryRedisKey = getRedisApiKey({
      masterConfigData: masterConfigData,
      redisPrefix: "PREFIX_REDIS_ROS",
      params: {
        api: "latestcontentfeed",
        page: 0,
        display: 5,
        type: "story",
        unixtime,
      },
    });
    const latestVideoRedisKey = getRedisApiKey({
      masterConfigData: masterConfigData,
      redisPrefix: "PREFIX_REDIS_ROS",
      params: {
        api: "latestcontentfeed",
        page: 0,
        display: 5,
        type: "video",
        unixtime,
      },
    });

    // let tourID = 8307; //2025 tour id
    const tour_id = iplYearsFilter[year];
    const statsId = pageId == "orange" ? 10 : 13;
    const getStats = async () => {
      try {
        const statsData = await getStatsPlayer(tour_id, statsId);

        if (statsData) {
          return statsData;
        }
        return null;
      } catch (err) {
        logger.error(err);
      }
    };
    let [
      categoryNewsListData,
      latestStoryData,
      latestVideoData,
      faqData,
      statsData,
    ] = await Promise.all([
      fetchRedisWrapper(categoryListRedisKey, () =>
        getDataByUrl({
          url: "/sports",
          page: page,
          display: 6,
          type: "story",
        })
      ),
      fetchRedisWrapper(latestStoryRedisKey, () =>
        getLatestContentFeed({
          type: "story",
          page: 0,
          display: 5,
          unixtime,
        })
      ),
      fetchRedisWrapper(latestVideoRedisKey, () =>
        getLatestContentFeed({
          type: "video",
          page: 0,
          display: 5,
          unixtime,
        })
      ),
      fetchRedisWrapper(faqRedisKey, () =>
        getFaqBySlug({
          url: "ipl-2025-faqs",
        })
      ),
      getStats(),
    ]);

    setPageTTL(ctx, 120);
    return {
      props: {
        categoryNewsListData,
        latestStoryData,
        latestVideoData,
        faqData,
        pageId,
        year,
        statsData,
      },
    };
  } catch (error) {
    logger.error(error);
    return {
      notFound: true,
    };
  }
};
